import { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { HomeIcon } from "@heroicons/react/24/outline";
import * as InstitutesServices from "../../services/institutes.services";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { logout } from "../../slices/authSlice";
import PageLoader from "../../components/Loaders/PageLoader";
import Layout from "../../components/Layout/Layout";
import Container from "../../components/Layout/Container";
import SearchBar from "../../components/SearchBar";

const InstitutesList = () => {
  const [institutes, setInstitutes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(10);

  const { t } = useTranslation();
  let dispatch = useDispatch();

  useEffect(() => {
    getInstitutesUsers(1);
    // eslint-disable-next-line
  }, [filter]);

  const columns = [
    {
      name: (
        <div className="text-sm font-semibold text-gray-900 ltr:ml-4 rtl:mr-4">
          {t("name")}
        </div>
      ),
      selector: (row) => (
        <div className="flex flex-col ltr:ml-4 rtl:mr-4">
          <div className="text-sm font-semibold text-gray-900 pt-2">
            {row?.englishName}
          </div>
          <div className="text-sm text-gray-500 pb-2">{row?.arabicName}</div>
        </div>
      ),
      sortable: true,
      width: "250px",
    },
    {
      name: (
        <div className="text-sm font-semibold text-gray-900">{t("email")}</div>
      ),
      selector: (row) => row.user?.email,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <div className="text-sm font-semibold text-gray-900">
          {t("address")}
        </div>
      ),
      selector: (row) => row?.address,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <div className="text-sm font-semibold text-gray-900">
          {t("phoneNumber")}
        </div>
      ),
      selector: (row) => <div>{row?.phoneNumber}</div>,
      width: "200px",
    },
    {
      name: (
        <div className="text-sm font-semibold text-gray-900">{t("admin")}</div>
      ),
      selector: (row) => row.user?.firstName,
      sortable: true,
      width: "150px",
    },
    {
      name: (
        <div className="text-sm font-semibold text-gray-900">
          {t("actions")}
        </div>
      ),
      selector: (row) => (
        <div className="flex space-x-6">
          <Link
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-secondary text-base font-medium text-white hover:bg-secondary sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-75"
            to={`/champion/institutes/${row?.id}`}
          >
            {t("details")}
          </Link>
        </div>
      ),
      width: "150px",
    },
  ];

  const getInstitutesUsers = async (page) => {
    setLoading(true);
    try {
      const response = await InstitutesServices.getAllInstitutes(
        page,
        limit,
        filter
      );
      if (response.status === 200) {
        setInstitutes(response.data?.institutes?.rows);
        setCount(response.data?.institutes?.count);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      if (error.response?.status === 401) {
        dispatch(logout());
      }
    }
  };

  const handlePageChange = (page) => {
    getInstitutesUsers(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setLoading(true);
    try {
      const response = await InstitutesServices.getAllInstitutes(
        page,
        newPerPage,
        filter
      );
      setInstitutes(response.data?.institutes?.rows);
      setLimit(newPerPage);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
    }
  };

  return (
    <Layout>
      {/* Breadcrumb */}
      <nav
        className="flex border-b border-gray-200 bg-white"
        aria-label="Breadcrumb"
      >
        <ol className="mx-auto flex w-full max-w-screen-xl space-x-4 md:px-8 sm:px-6 lg:px-8">
          <li className="flex">
            <div className="flex items-center">
              <Link
                to={"/"}
                className="text-gray-500 hover:text-secondary mx-2"
              >
                <HomeIcon
                  className="h-5 w-5 flex-shrink-0"
                  aria-hidden="true"
                />
                <span className="sr-only">{t("home")}</span>
              </Link>
            </div>
          </li>
          <li className="flex">
            <div className="flex items-center">
              <svg
                className="h-full w-6 flex-shrink-0 text-gray-200"
                viewBox="0 0 24 44"
                preserveAspectRatio="none"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
              </svg>
              <p className="ltr:ml-4 rtl:mr-4 text-sm font-semibold rtl:text-xs rtl:font-medium text-secondary hover:text-primary">
                {t("institutes")}
              </p>
            </div>
          </li>
        </ol>
      </nav>

      <Container>
        {/* Main content */}
        <div className="flex">
          <h1 className="flex-1 text-2xl font-bold text-gray-900">
            {t("institutesList")}
          </h1>
        </div>
        <div className="mt-8">
          <SearchBar
            filter={filter}
            setFilter={setFilter}
            placeholder={t("search")}
            className="w-full"
          />
        </div>
        <div className="mt-6 shadow-lg border border-gray-100">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="min-w-full py-2 align-middle md:px-6 lg:px-8 ">
              {institutes ? (
                <DataTable
                  columns={columns}
                  data={institutes && institutes}
                  progressPending={loading}
                  progressComponent={<PageLoader />}
                  pagination
                  paginationServer
                  paginationTotalRows={count}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  fixedHeader
                  direction={
                    window.localStorage.getItem("i18nextLng") === "en"
                      ? "ltr"
                      : "rtl"
                  }
                />
              ) : (
                <PageLoader />
              )}
            </div>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default InstitutesList;
